import Head from 'next/head'
import { useRouter } from 'next/router'
import { Venue } from 'types/model'

import { imageOptimize } from '@utils/images'
import { env } from '@lib/env'
import { useMemo } from 'react'

export function VenueMeta({ venue }: { venue: Venue }) {
  const router = useRouter()
  const pageTitle = venue.name
  const description = venue.details.shortDescription ?? venue.details.description

  const image = useMemo(
    () =>
      imageOptimize(venue.primaryImage?.filename, {
        w: '1200',
        h: '627',
        q: '20',
        dpr: '0.5',
        fm: 'webp',
        fit: 'fill',
        fill: 'blur',
      }),
    [venue]
  )

  const keywords = useMemo(
    () => [
      'Nightlife',
      'Night life',
      'Venue',
      'Late night',
      'Club',
      'Tablelist',
      'Tablelist Pro',
      venue.name,
      ...(venue.location.address
        ? [venue.location.address.city, venue.location.address.state, venue.location.address.zip]
        : []),
    ],
    [venue.location.address, venue.name]
  )
  const url = useMemo(() => `${env.domain}/${router.asPath.split('?')[0]}`, [router.asPath])

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={venue.name} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />

      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
    </Head>
  )
}
